import { useDeferredValue, useMemo } from 'react'
import { MagnifyingGlassIcon } from '@radix-ui/react-icons'
import {
  Badge,
  Button,
  Checkbox,
  Input,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui'
import { getOfficeHoursValues } from './officeHoursForm'

function LoadingRows() {
  return Array.from({ length: 6 }, (_, rowIndex) => (
    <TableRow key={rowIndex}>
      {Array.from({ length: 8 }, (__, cellIndex) => (
        <TableCell key={cellIndex}>
          <Skeleton className="h-8 w-full" />
        </TableCell>
      ))}
    </TableRow>
  ))
}

function RowCheckbox({ checked, onCheckedChange, label, disabled }) {
  return (
    <Checkbox
      checked={checked}
      onCheckedChange={onCheckedChange}
      aria-label={label}
      disabled={disabled}
    />
  )
}

export function OfficeHoursTable({
  rows,
  drafts,
  selectedIds,
  search,
  onSearchChange,
  onToggleBuilding,
  onToggleVisible,
  onChangeRow,
  onSave,
  isLoading,
  isSaving,
}) {
  const deferredSearch = useDeferredValue(search.trim().toLowerCase())
  const filteredRows = useMemo(() => {
    if (!deferredSearch) return rows
    return rows.filter((row) =>
      `${row.portfolioName} ${row.building} ${row.timeZone || ''}`
        .toLowerCase()
        .includes(deferredSearch),
    )
  }, [deferredSearch, rows])
  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds])
  const visibleIds = filteredRows.map(({ buildingId }) => Number(buildingId))
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((buildingId) => selectedIdSet.has(buildingId))
  const dirtyCount = Object.keys(drafts).length

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full sm:max-w-md">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Search portfolio, building, or timezone"
            className="pl-9"
          />
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline">{selectedIds.length} selected</Badge>
          <Badge variant={dirtyCount ? 'secondary' : 'outline'}>{dirtyCount} edited</Badge>
          <Button type="button" onClick={onSave} disabled={!dirtyCount || isSaving}>
            {isSaving ? 'Saving...' : 'Save table edits'}
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-md border border-border">
        <Table className="min-w-[1320px]">
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">
                <RowCheckbox
                  checked={allVisibleSelected}
                  onCheckedChange={() => onToggleVisible(visibleIds, allVisibleSelected)}
                  label="Select visible buildings"
                  disabled={!visibleIds.length}
                />
              </TableHead>
              <TableHead>Portfolio</TableHead>
              <TableHead>Building</TableHead>
              <TableHead className="w-24">Enabled</TableHead>
              <TableHead className="w-36">Office opens</TableHead>
              <TableHead className="w-36">After hours start</TableHead>
              <TableHead className="min-w-56">Timezone</TableHead>
              <TableHead className="min-w-80">Message</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <LoadingRows />
            ) : filteredRows.length ? (
              filteredRows.map((row) => {
                const buildingId = Number(row.buildingId)
                const values = getOfficeHoursValues(row, drafts[buildingId])
                const isSelected = selectedIdSet.has(buildingId)
                const isConfigured = Boolean(
                  row.startTime && row.endTime && row.timeZone && row.messageTemplate,
                )

                return (
                  <TableRow key={buildingId} className={isSelected ? 'bg-muted/40' : ''}>
                    <TableCell>
                      <RowCheckbox
                        checked={isSelected}
                        onCheckedChange={() => onToggleBuilding(buildingId)}
                        label={`Select ${row.building || `building ${buildingId}`}`}
                      />
                    </TableCell>
                    <TableCell className="font-medium" data-pii="property">
                      {row.portfolioName}
                    </TableCell>
                    <TableCell data-pii="property">
                      <div>{row.building || `Building ${buildingId}`}</div>
                      <Badge
                        variant={drafts[buildingId] ? 'secondary' : 'outline'}
                        className="mt-1"
                      >
                        {drafts[buildingId]
                          ? 'Edited'
                          : isConfigured
                          ? 'Configured'
                          : 'Not configured'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <RowCheckbox
                        checked={values.afterHoursEnabled}
                        onCheckedChange={(checked) =>
                          onChangeRow(buildingId, 'afterHoursEnabled', checked === true)
                        }
                        label={`Enable after-hours messages for ${
                          row.building || `building ${buildingId}`
                        }`}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="time"
                        value={values.startTime}
                        onChange={(event) =>
                          onChangeRow(buildingId, 'startTime', event.target.value)
                        }
                        aria-label={`Office opening time for ${
                          row.building || `building ${buildingId}`
                        }`}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="time"
                        value={values.endTime}
                        onChange={(event) => onChangeRow(buildingId, 'endTime', event.target.value)}
                        aria-label={`After-hours start time for ${
                          row.building || `building ${buildingId}`
                        }`}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={values.timeZone}
                        onChange={(event) =>
                          onChangeRow(buildingId, 'timeZone', event.target.value)
                        }
                        list="office-hours-timezones"
                        aria-label={`Timezone for ${row.building || `building ${buildingId}`}`}
                      />
                    </TableCell>
                    <TableCell>
                      <textarea
                        value={values.messageTemplate}
                        onChange={(event) =>
                          onChangeRow(buildingId, 'messageTemplate', event.target.value)
                        }
                        rows={2}
                        maxLength={10000}
                        aria-label={`After-hours message for ${
                          row.building || `building ${buildingId}`
                        }`}
                        data-pii="content"
                        className="flex min-h-16 w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      />
                    </TableCell>
                  </TableRow>
                )
              })
            ) : (
              <TableRow>
                <TableCell colSpan={8} className="h-28 text-center text-sm text-muted-foreground">
                  {rows.length
                    ? 'No buildings match this search.'
                    : 'No active buildings were found for this account.'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
